import { sendBackgroundMessage } from '$tablet-app/src/app/chat/functions';
import {
  useSaveCommand,
  useUpdateScreenChatContext,
} from '$tablet-app/src/app/chat/hooks';
import { intent_labels } from '$tablet-app/src/app/chat/intent_labels';
import { TaskItemForm } from '$tablet-app/src/app/components/TaskItemForm/TaskItemForm';
import { DeveloperScreenInfo } from '$tablet-app/src/app/developer/components/DeveloperScreenInfo/DeveloperScreenInfo';
import { JSONText } from '$tablet-app/src/app/developer/components/JSONText';
import { postCreateRecordLogEvent } from '$tablet-app/src/app/logging/functions/postCreateRecordLogEvent';
import { RootStackScreenProps } from '$tablet-app/src/app/navigation/RootStackNavigator/RootStackNavigator';
import {
  useCurrentUserID,
  useListCurrentHabitatUsers,
} from '$tablet-app/src/app/users';
import { FeatureDomain, TaskListType } from '@akin-home/amplify';
import { ChecklistModel, createChecklist } from '@akin-home/checklists';
import {
  useCreateTaskList,
  useListTaskListsByHabitat,
} from '@akin-home/checklists/frontend';
import { useHabitatID } from '@akin-home/habitats/react';
import { showInfoToast, showSuccessToast } from '@akin-home/toast/native';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUpdateChecklistFormCommand } from '../../hooks';

const context = { canSave: true, fields: { title: 'text' } };

const parameters = {
  defaults: {
    type: 'Shopping or ToDo or Checklist',
    title: 'text',
    emoji: 'emoji',
  },
};

export const CreateChecklistScreen = (
  props: RootStackScreenProps<'CreateChecklist'>
) => {
  const {
    navigation: { goBack },
    route: { params },
  } = props;

  const { data: users } = useListCurrentHabitatUsers();

  const user_id = useCurrentUserID();
  const habitatID = useHabitatID();

  const { lists, isSuccess } = useListTaskListsByHabitat({
    habitatID,
  });

  const [checklist, setChecklist] = useState<ChecklistModel>(
    createChecklist({
      principalID: user_id,
      habitatID,
      ...params?.defaults,
      type: params?.defaults?.type || TaskListType.Checklist,
    })
  );

  useUpdateScreenChatContext(context);
  useUpdateChecklistFormCommand(setChecklist);

  const create = useCreateTaskList();

  const show_main_shopping_list_checkbox =
    checklist?.primaryShoppingList ||
    (checklist?.type === 'Shopping' &&
      isSuccess &&
      !lists.some((l) => l.primaryShoppingList));

  const onSave = useSaveCommand(
    useCallback(async () => {
      showInfoToast({ text1: 'Saving List' });
      goBack();

      const result = await create(checklist);

      postCreateRecordLogEvent({
        user_id,
        feature_domain: FeatureDomain.CHECKLISTS,
        data: {
          id: result.data.createTaskList?.id,
          model: 'Checklist',
        },
      });

      showSuccessToast({ text1: 'List Saved!' });
    }, [checklist, create, goBack, user_id])
  );

  const listNames = useMemo(
    () =>
      lists.length > 0
        ? lists.map((list) => list.title.toLowerCase().trim())
        : [],
    [lists]
  );

  useEffect(() => {
    sendBackgroundMessage(intent_labels.open_create_checklist_screen);
  }, []);

  return (
    <>
      <TaskItemForm
        mode="create"
        users={users}
        item={checklist}
        onChange={setChecklist}
        show_main_shopping_list_checkbox={show_main_shopping_list_checkbox}
        listNames={listNames}
        onSave={onSave}
        onCancel={goBack}
      />

      <DeveloperScreenInfo
        context={<JSONText>{context}</JSONText>}
        parameters={<JSONText>{parameters}</JSONText>}
      />
    </>
  );
};
